<template>
  <div class="region-shape-form" v-loading="loadingShape">
    <div class="hint-block">
      <div class="hint-title">坐标输入说明</div>
      <p>每行一个点：先输入 <strong>X</strong>，然后空格输入 <strong>Z</strong>，例如 <code>-672 -756</code>。</p>
      <p>可直接从 BlueMap 复制点坐标后粘贴，多行即可圈出区域。</p>
    </div>

    <el-form label-position="top" @submit.prevent>
      <el-form-item label="行政区域" required>
        <el-select
          v-model="selectedRegionId"
          placeholder="选择行政区域"
          style="width: 100%;"
          @change="handleRegionChange"
          filterable
        >
          <el-option
            v-for="region in regions"
            :key="region.id"
            :label="`${region.chinese_name}${region.english_name ? ' | ' + region.english_name : ''}`"
            :value="region.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="形状平面高度 (shape-y)">
        <el-input-number
          v-model="shapeY"
          :min="-2000"
          :max="4000"
          :step="1"
          controls-position="right"
          style="width: 160px;"
        />
        <span class="hint-text">与建筑高度接近的Y值，决定区域渲染平面</span>
      </el-form-item>

      <el-form-item
        label="区域坐标点（每行一个：X 空格 Z）"
        :error="pointError"
        required
      >
        <el-input
          v-model="pointsText"
          type="textarea"
          :autosize="{ minRows: 6, maxRows: 12 }"
          placeholder="-672 -756"
          @input="validatePoints"
        />
        <div class="preview-row">
          <span>已解析 {{ parsedPoints.length }} 个点</span>
          <span v-if="parsedPoints.length >= 3" class="ok-text">可生成闭合区域</span>
        </div>
      </el-form-item>

      <el-form-item label="标签 HTML（可选）">
        <el-input
          v-model="labelHtml"
          type="textarea"
          :autosize="{ minRows: 2, maxRows: 4 }"
          placeholder="自定义区域标签，不填则使用区域名"
        />
      </el-form-item>

      <el-form-item label="填充颜色 (RGBA)">
        <div class="color-row">
          <el-color-picker v-model="fillColorDisplay" show-alpha />
          <div class="color-inputs">
            <el-input-number v-model="fillColor.r" :min="0" :max="255" :controls="false" size="small" placeholder="R" />
            <el-input-number v-model="fillColor.g" :min="0" :max="255" :controls="false" size="small" placeholder="G" />
            <el-input-number v-model="fillColor.b" :min="0" :max="255" :controls="false" size="small" placeholder="B" />
            <el-input-number v-model="fillColor.a" :min="0" :max="1" :step="0.05" :controls="false" size="small" placeholder="A" />
          </div>
        </div>
      </el-form-item>

      <el-form-item label="文字颜色 (RGBA)">
        <div class="color-row">
          <el-color-picker v-model="labelColorDisplay" show-alpha />
          <div class="color-inputs">
            <el-input-number v-model="labelColor.r" :min="0" :max="255" :controls="false" size="small" placeholder="R" />
            <el-input-number v-model="labelColor.g" :min="0" :max="255" :controls="false" size="small" placeholder="G" />
            <el-input-number v-model="labelColor.b" :min="0" :max="255" :controls="false" size="small" placeholder="B" />
            <el-input-number v-model="labelColor.a" :min="0" :max="1" :step="0.05" :controls="false" size="small" placeholder="A" />
          </div>
        </div>
      </el-form-item>
    </el-form>

    <div class="preview-chip" v-if="activeRegion">
      <div class="color-chip" :style="{ background: regionFillPreview }"></div>
      <div class="color-chip border" :style="{ borderColor: regionLinePreview }"></div>
      <div class="chip-text">
        <div class="name">{{ activeRegion.chinese_name }}</div>
        <div class="sub" v-if="activeRegion.english_name">{{ activeRegion.english_name }}</div>
      </div>
    </div>

    <div class="form-actions">
      <el-button @click="emitCancel" :disabled="saving">取消</el-button>
      <el-button type="primary" :disabled="!canSave" :loading="saving" @click="handleSave">保存行政区规划</el-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { regionShapeApi } from '../api'

const props = defineProps({
  regions: {
    type: Array,
    default: () => []
  },
  defaultRegionId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['success', 'cancel'])

const selectedRegionId = ref(props.defaultRegionId)
const pointsText = ref('')
const shapeY = ref(64)
const labelHtml = ref('')
const fillColor = ref({ r: 0, g: 0, b: 0, a: 0.4 })
const labelColor = ref({ r: 0, g: 0, b: 0, a: 0.8 })
const pointError = ref('')
const loadingShape = ref(false)
const saving = ref(false)

const activeRegion = computed(() => props.regions.find(r => r.id === selectedRegionId.value))

const regionLinePreview = computed(() => {
  const color = activeRegion.value?.line_color || { r: 255, g: 120, b: 0, a: 1 }
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`
})

const regionFillPreview = computed(() => {
  const color = fillColor.value
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 0.4})`
})

const fillColorDisplay = computed({
  get() {
    return `rgba(${fillColor.value.r}, ${fillColor.value.g}, ${fillColor.value.b}, ${fillColor.value.a})`
  },
  set(val) {
    try {
      const rgba = val.replace(/[^\d.,]/g, '').split(',').map(Number)
      fillColor.value = { r: rgba[0] || 0, g: rgba[1] || 0, b: rgba[2] || 0, a: (rgba[3] ?? 0.4) }
    } catch (e) {
      // ignore parse errors
    }
  }
})

const labelColorDisplay = computed({
  get() {
    return `rgba(${labelColor.value.r}, ${labelColor.value.g}, ${labelColor.value.b}, ${labelColor.value.a})`
  },
  set(val) {
    try {
      const rgba = val.replace(/[^\d.,]/g, '').split(',').map(Number)
      labelColor.value = { r: rgba[0] || 0, g: rgba[1] || 0, b: rgba[2] || 0, a: (rgba[3] ?? 0.8) }
    } catch (e) {
      // ignore parse errors
    }
  }
})

const getDefaultFillColor = () => {
  const base = activeRegion.value?.fill_color || activeRegion.value?.line_color || { r: 255, g: 120, b: 0, a: 0.4 }
  return { r: base.r ?? 0, g: base.g ?? 0, b: base.b ?? 0, a: base.a ?? 0.4 }
}

const getDefaultLabelColor = () => {
  const base = activeRegion.value?.line_color || { r: 255, g: 120, b: 0, a: 0.8 }
  const alpha = base.a !== undefined && base.a !== null ? base.a * 0.8 : 0.8
  return { r: base.r ?? 0, g: base.g ?? 0, b: base.b ?? 0, a: alpha }
}

const parsedPoints = computed(() => {
  const lines = pointsText.value
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
  return lines
    .map(line => {
      const [x, z] = line.split(/\s+/)
      return { x: Number(x), z: Number(z) }
    })
    .filter(p => !Number.isNaN(p.x) && !Number.isNaN(p.z))
})

const canSave = computed(() => {
  return !!selectedRegionId.value && parsedPoints.value.length >= 3 && !pointError.value
})

const emitCancel = () => emit('cancel')

const validatePoints = () => {
  if (parsedPoints.value.length === 0) {
    pointError.value = '至少需要 3 个坐标点'
    return
  }
  if (parsedPoints.value.length < 3) {
    pointError.value = '坐标点不足以形成闭合区域'
  } else {
    pointError.value = ''
  }
}

const loadShape = async (regionId) => {
  if (!regionId) return
  loadingShape.value = true
  try {
    const result = await regionShapeApi.getByRegion(regionId)
    const data = result.data
    if (data && data.coordinates) {
      pointsText.value = data.coordinates.map(p => `${p.x} ${p.z}`).join('\n')
      shapeY.value = data.shape_y ?? 64
      labelHtml.value = data.label_html || ''
      fillColor.value = data.fill_color || getDefaultFillColor()
      labelColor.value = data.label_color || getDefaultLabelColor()
    } else {
      pointsText.value = ''
      labelHtml.value = ''
      fillColor.value = getDefaultFillColor()
      labelColor.value = getDefaultLabelColor()
    }
    validatePoints()
  } catch (error) {
    pointsText.value = ''
    pointError.value = ''
    console.warn('加载行政区规划失败', error)
  } finally {
    loadingShape.value = false
  }
}

const handleRegionChange = (val) => {
  selectedRegionId.value = val
  loadShape(val)
}

const handleSave = async () => {
  validatePoints()
  if (!canSave.value) return
  saving.value = true
  try {
    const result = await regionShapeApi.save({
      region_id: selectedRegionId.value,
      coordinates: parsedPoints.value,
      shape_y: shapeY.value,
      label_html: labelHtml.value?.trim() || null,
      fill_color: fillColor.value,
      label_color: labelColor.value
    })
    ElMessage.success(result.message || '行政区规划已保存，后台正在匹配车站行政区')
    emit('success', { region_id: selectedRegionId.value })
  } catch (error) {
    console.error('保存行政区规划失败', error)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (selectedRegionId.value) {
    loadShape(selectedRegionId.value)
  } else {
    fillColor.value = getDefaultFillColor()
    labelColor.value = getDefaultLabelColor()
  }
})

watch(() => props.defaultRegionId, (val) => {
  if (!selectedRegionId.value && val) {
    selectedRegionId.value = val
    loadShape(val)
  }
})
</script>

<style scoped>
.region-shape-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hint-block {
  background: #fff7ed;
  border: 1px solid #fde6d2;
  border-radius: 8px;
  padding: 12px 14px;
  color: #9a5a1a;
  line-height: 1.5;
}

.hint-title {
  font-weight: 700;
  margin-bottom: 4px;
}

.hint-text {
  margin-left: 12px;
  color: #909399;
  font-size: 12px;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
  margin-top: 4px;
}

.ok-text {
  color: #67c23a;
  font-weight: 600;
}

.preview-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fafafa;
}

.color-chip {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.color-chip.border {
  background: #fff;
}

.chip-text .name {
  font-weight: 600;
}

.chip-text .sub {
  font-size: 12px;
  color: #909399;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 4px;
}

.color-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-inputs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
